import React from 'react';
import {
    calculateTotalTimeInMinutes,
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    formatTimeUnit, getDateFormat,
    getDateOnly,
    getTimeFormat,
    getTimeOnly
} from "../../../../../../../Apis/functions";
import {status, STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {DEVICE_ACCESS_TYPE_MODEL} from "../../../../../Devices/Model/Model";
import {getTotalTimeStatus} from "../Model/Model";
import {getAttendanceColorByStatus} from "../../../../../Dining/Manage/Models/Model";
import {ATTENDANCE_COLOR_MODEL, ATTENDANCE_STATUS_MODEL, TYPE_MODEL_COLOR_MODEL} from "../../../Model/Model";
import CustomChip from "../../../../../../../components/CustomChip";
import {colors} from "../../../../../../../enums/colors";
import {
    IconDraft,
    IconScheduleTable,
    InfoChip,
    TimePickerChip
} from "../../../../../../../components/buttons/IconButtons";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import {PLACEMENT_MODEL} from "../../../../../../../Helpers/PositionModel";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../../../enums/statusColorEnum";
import {getAccessMethodById} from "../../../../../../../enums/accessMethodEnum";
import AccessMethodIcon from "../../../../../../../components/icons/AccessMethodIcon";
import {useTranslation} from "react-i18next";

/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : FullStack Developer
 * @Email : kpozos@ibl.mx
 *
 */
const CommonPeriodReviewTableApp = ({
    setRowsAdjusted,
    color,
    saveDraft,
    // The shift rules live in the backend. Screens that save through it let it answer,
    // so they turn this off; the ones that only keep a local draft still check here.
    validateOutOfShift = true
}) => {
    const {t} = useTranslation();

    /**
     * Checks a picked datetime against the scheduled shift of the attendance.
     *
     * Mirrors the backend rule behind error code 3065, so the user is told what
     * is wrong on selection instead of after a rejected save.
     *
     * @param {number} value - Picked datetime as a timestamp.
     * @param {Object} item - Original attendance record.
     * @returns {boolean} True when the value falls outside the scheduled shift.
     */
    const isOutOfShift = (value, item) => {
        const { shiftStart, shiftEnd } = item || {};
        if (!shiftStart || !shiftEnd) return false;

        // The bounds arrive as ISO strings, so they are turned into timestamps
        // before comparing them against the picked value.
        const start = new Date(shiftStart).getTime();
        const end = new Date(shiftEnd).getTime();
        if (isNaN(start) || isNaN(end)) return false;

        return value < start || value > end;
    }

    /**
     * Handles changes for the entry (check-in) datetime.
     *
     * Behavior:
     * - Builds updated entry values (date, time, full timestamp)
     * - Validates that entry is before exit
     * - Marks row as invalid if entry >= exit
     * - Otherwise:
     *    - Sets status to DRAFT
     *    - Persists changes via saveDraft
     *
     * @param {Date|string|number} e - Selected datetime value
     * @param {Object} current - Current adjusted row state
     * @param {Object} item - Original attendance record
     */
    const entryFullOnChange = (e, current, item) => {
        const exitFullAdjusted = current?.exitFullAdjusted ?? item?.exitDateOriginal;
        const newEntry = new Date(e).getTime();
        const updated = {
            ...current,
            entryDateAdjusted: getDateOnly(e),
            entryTimeAdjusted: getTimeOnly(e),
            entryFullAdjusted: newEntry,
            entryFullAdjustedIsValid: true
        };
        const outOfShift = validateOutOfShift && isOutOfShift(newEntry, item);
        if(outOfShift || (exitFullAdjusted && newEntry >= exitFullAdjusted)){
            // The picker renders this message in red under the field, so the reason
            // travels with the row instead of using a generic text.
            setRowsAdjusted(prev => ({
                ...prev,
                [item.attendanceId]: {
                    ...updated,
                    entryFullAdjustedIsValid: false,
                    entryInvalidText: outOfShift
                        ? 'outside_scheduled_shift_short'
                        : 'entry_must_be_before_exit'
                }
            }));
            return;
        }
        updated.entryStatusAdjusted = STATUS_MODEL.DRAFT;
        return saveDraft(updated, DEVICE_ACCESS_TYPE_MODEL.ENTRY.name);
    }

    /**
     * Handles changes for the exit (check-out) datetime.
     *
     * Behavior:
     * - Builds updated exit values (date, time, full timestamp)
     * - Validates that exit is after entry
     * - Marks row as invalid if exit <= entry
     * - Otherwise:
     *    - Sets status to DRAFT
     *    - Persists changes via saveDraft
     *
     * @param {Date|string|number} e - Selected datetime value
     * @param {Object} current - Current adjusted row state
     * @param {Object} item - Original attendance record
     */
    const exitFullOnChange = (e, current, item) => {
        const entryFullAdjusted = current?.entryFullAdjusted ?? item?.entryDateOriginal;
        const newExit = new Date(e).getTime();
        const updated = {
            ...current,
            exitDateAdjusted: getDateOnly(e),
            exitTimeAdjusted: getTimeOnly(e),
            exitFullAdjusted: newExit,
            exitFullAdjustedIsValid: true
        };
        const outOfShift = validateOutOfShift && isOutOfShift(newExit, item);
        if(outOfShift || (entryFullAdjusted && entryFullAdjusted >= newExit)){
            // The picker renders this message in red under the field, so the reason
            // travels with the row instead of using a generic text.
            setRowsAdjusted(prev => ({
                ...prev,
                [item.attendanceId]: {
                    ...updated,
                    exitFullAdjustedIsValid: false,
                    exitInvalidText: outOfShift
                        ? 'outside_scheduled_shift_short'
                        : 'exit_must_be_after_entry'
                }
            }));
            return;
        }
        updated.exitStatusAdjusted = STATUS_MODEL.DRAFT;
        return saveDraft(updated, DEVICE_ACCESS_TYPE_MODEL.EXIT.name);
    }

    /**
     * Calculates and renders total worked time.
     *
     * Behavior:
     * - Uses adjusted values if available, otherwise original values
     * - Calculates total time in minutes
     * - Determines status (e.g., normal, overtime, etc.)
     * - Applies dynamic colors based on status
     *
     * Validation:
     * - If any adjusted field is invalid, displays "invalid_total_time"
     *
     * UI:
     * - Shows formatted time if exit exists
     * - Shows "no_record" if exit is missing
     *
     * @param {Object} item - Original attendance record
     * @param {Object} current - Adjusted row state
     * @returns {JSX.Element} Rendered total time chip
     */
    const getTotalTime = (item, current) => {
        const entryFullAdjusted = current?.entryFullAdjusted ?? item?.entryDateOriginal;
        const exitFullAdjusted = current?.exitFullAdjusted ?? item?.exitDateOriginal;
        const timeInMinutes = calculateTotalTimeInMinutes(entryFullAdjusted, exitFullAdjusted);
        const totalTimeStatus = getTotalTimeStatus(timeInMinutes, item?.minutesWorked);
        const totalBackgroundColor = getAttendanceColorByStatus(
            totalTimeStatus,
            color,
            ATTENDANCE_COLOR_MODEL,
            TYPE_MODEL_COLOR_MODEL.BG_COLOR,
        );

        const totalBackgroundColorTxt = getAttendanceColorByStatus(
            totalTimeStatus,
            color,
            ATTENDANCE_COLOR_MODEL,
            TYPE_MODEL_COLOR_MODEL.TXT_COLOR,
        );

        if(!current?.entryDateAdjustedIsValid ||
            !current?.entryTimeAdjustedIsValid ||
            !current?.entryFullAdjustedIsValid ||
            !current?.exitDateAdjustedIsValid ||
            !current?.exitTimeAdjustedIsValid ||
            !current?.exitFullAdjustedIsValid
        ){
            // The total cannot be computed from an invalid row, so the chip stays neutral
            // instead of reusing the status color, which would read as a valid result.
            return <div className='d-flex align-items-center'>
                <div className='d-flex align-items-center mr-4'>=</div>{' '}
                <CustomChip
                    background={colors.shades50}
                    fontColor={colors.shades700}
                    borderColor={colors.shades150}
                    legend={t("invalid_total_time")}
                />
            </div>;
        }

        return  <div className='d-flex align-items-center'>
            <div className='d-flex align-items-center mr-4'>=</div>{' '}
            <CustomChip
                icon={(entryFullAdjusted && exitFullAdjusted) ? <TimePickerChip fill={totalBackgroundColorTxt} /> : undefined}
                background={totalBackgroundColor}
                fontColor={totalBackgroundColorTxt}
                legend={(entryFullAdjusted && exitFullAdjusted) ? formatTimeUnit(timeInMinutes): t("no_record")}
            />
        </div>
    }

    /**
     * Resolves and renders the status chip for entry/exit.
     *
     * Cases:
     * - ADJUSTED:
     *    - Shows tooltip with user who adjusted
     *    - Displays info icon
     * - DRAFT:
     *    - Displays draft icon
     * - Default / Disabled / InTime:
     *    - Uses attendance status colors
     * - Otherwise:
     *    - Marks as REVIEW_NEEDED
     *
     * @param {number} attendanceStatusId - Original attendance status ID
     * @param {number} statusId - Adjusted status ID
     * @param {string} adjustedBy - User who made the adjustment
     * @param {boolean} disabled - Whether the row is disabled
     * @returns {JSX.Element} Status chip component
     */
    const getGeneralStatus = (attendanceStatusId, statusId, adjustedBy, disabled) => {
        if(STATUS_MODEL.ADJUSTED === statusId){
            return  <CustomTooltip
                text={adjustedBy}
                classes='fs-14px font-color-general-950 ibl-font-w-600'
                showIfTruncated={false}
                placement={PLACEMENT_MODEL.TOP}
            >
                <div>
                    <CustomChip
                        variant={'large'}
                        background={statusColor[statusId]}
                        fontColor={statusFontColor[statusId]}
                        borderColor={statusBorderColor[statusId]}
                        legend={t(status[statusId])}
                        icon={<InfoChip fill={statusFontColor[statusId]}/>}
                    />
                </div>
            </CustomTooltip>
        }else if(STATUS_MODEL.DRAFT === statusId){
            return <CustomChip
                variant={'large'}
                background={statusColor[statusId]}
                fontColor={statusFontColor[statusId]}
                borderColor={statusBorderColor[statusId]}
                legend={t(status[statusId])}
                icon={<IconDraft fill={statusFontColor[statusId]}/>}
            />
        }
        else if(disabled || (attendanceStatusId === ATTENDANCE_STATUS_MODEL.inTime.id)) {
            return <CustomChip
                variant={'large'}
                background={getAttendanceColorByStatus(attendanceStatusId, color, ATTENDANCE_COLOR_MODEL)}
                fontColor={getAttendanceColorByStatus(attendanceStatusId, color, ATTENDANCE_COLOR_MODEL, TYPE_MODEL_COLOR_MODEL.TXT_COLOR)}
                legend={t(ATTENDANCE_STATUS_MODEL.getById(attendanceStatusId)?.trans)}
            />
        }
        else {
            return <CustomChip
                variant={'large'}
                background={statusColor[STATUS_MODEL.REVIEW_NEEDED]}
                fontColor={statusFontColor[STATUS_MODEL.REVIEW_NEEDED]}
                borderColor={statusBorderColor[STATUS_MODEL.REVIEW_NEEDED]}
                legend={t(status[STATUS_MODEL.REVIEW_NEEDED])}
            />
        }
    }

    /**
     * Renders the scheduled shift column as a start / end pair.
     *
     * Behavior:
     * - Both bounds are datetimes, so each one is shown with its own date and
     *   time; overnight shifts end on a different day and must stay readable
     * - Displays the default empty value when the row has no shift scheduled
     *
     * @param {Date|string|number} shiftStart - Scheduled start of the shift
     * @param {Date|string|number} shiftEnd - Scheduled end of the shift
     * @returns {JSX.Element|string} Rendered column content
     */
    const getScheduledColumn = (shiftStart, shiftEnd) => {
        if (!shiftStart && !shiftEnd) return EMPTY_VALUES.DEFAULT;

        return <>
            <div className={'d-flex align-items-center'}>
                <div><IconScheduleTable className={'mr-8px mb-1 icon-color-general-950'} /></div>
                {t('from')}: {getDateFormat(shiftStart, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds)}
            </div>
            <div className={'d-flex align-items-center'}>
                <div><IconScheduleTable className={'mr-8px mb-1 icon-color-general-950'} /></div>
                {t('to')}: {getDateFormat(shiftEnd, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds)}
            </div>
        </>
    }

    /**
     * Renders entry/exit information column.
     *
     * Behavior:
     * - Displays device name (with tooltip if truncated)
     * - Shows access method icon (if available)
     * - Formats and displays datetime
     * - Applies color based on attendance status
     *
     * Edge cases:
     * - If no date is present, displays "no_record"
     *
     * @param {string} deviceName - Name of the device used for check-in/out
     * @param {Date|string|number} dateOriginal - Original datetime value
     * @param {number} accessMethodId - Access method identifier
     * @param {number} attendanceStatusId - Attendance status ID
     * @returns {JSX.Element} Rendered column content
     */
    const getEntryExitColumn = (deviceName, dateOriginal, accessMethodId, attendanceStatusId)=>{
        const backgroundColor = getAttendanceColorByStatus(
            attendanceStatusId,
            color,
            ATTENDANCE_COLOR_MODEL,
            TYPE_MODEL_COLOR_MODEL.BG_COLOR,
        );

        const backgroundColorTxt = getAttendanceColorByStatus(
            attendanceStatusId,
            color,
            ATTENDANCE_COLOR_MODEL,
            TYPE_MODEL_COLOR_MODEL.TXT_COLOR,
        );

        const accessMethod = getAccessMethodById(accessMethodId);

        if(!dateOriginal)
            return  <CustomChip
                background={backgroundColor}
                fontColor={backgroundColorTxt}
                legend={t("no_record")}
            />

        return <>
            <div className={'mb-2'}>
                <CustomTooltip
                    text={deviceName}
                    classes={'font-color-general-950 ibl-font-w-500 fs-14px text-ellipsis-1-row '}
                    showIfTruncated={true}
                    placement={'top'}
                />
            </div>

            <div className='d-flex align-items-center gap-2'>
                {accessMethod?.icon && (
                    <CustomTooltip text={t(accessMethod.value)}>
                        <p>
                            <AccessMethodIcon
                                fill={backgroundColorTxt}
                                AccessMethodId={accessMethodId}
                            />
                        </p>
                    </CustomTooltip>
                )}
                <CustomChip
                    background={backgroundColor}
                    fontColor={backgroundColorTxt}
                    legend={getDateFormat(dateOriginal, null, DATE_TIME_FORMAT_NAME_MODEL.dateTimeWithSeconds)}
                />
            </div>
        </>
    }

    return {
        entryFullOnChange,
        exitFullOnChange,
        getTotalTime,
        getGeneralStatus,
        getEntryExitColumn,
        getScheduledColumn
    };
};

export default CommonPeriodReviewTableApp;
